import { useId } from 'react';
import { cn } from '@/lib/utils';

export type SparkTone = 'neutral' | 'negative' | 'caution' | 'positive';

/**
 * The design's shared colour mapping, applied to a sparkline: slate for neutral
 * volume, red for crash-related, amber for attention, green for healthy.
 */
const TONE: Record<SparkTone, string> = {
  neutral: 'text-muted-foreground',
  negative: 'text-pill-negative-foreground',
  caution: 'text-pill-caution-foreground',
  positive: 'text-pill-positive-foreground',
};

const W = 100;
const H = 26;
const PAD = 5;

/**
 * The design's curve, transcribed: points scaled to their own range, joined
 * by a Catmull-Rom spline turned into cubic Béziers, with a soft fill under it
 * and a dot on the newest point.
 */
function curve(values: number[]) {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const pts = values.map((v, i): [number, number] => [
    (i / (values.length - 1)) * W,
    H - PAD - ((v - min) / (max - min || 1)) * (H - PAD * 2),
  ]);
  const at = (i: number) => pts[Math.min(Math.max(i, 0), pts.length - 1)] ?? [0, 0];
  const f = (n: number) => n.toFixed(2);
  let line = `M${f(at(0)[0])},${f(at(0)[1])}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = at(i === 0 ? i : i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    line += ` C${f(c1[0] ?? 0)},${f(c1[1] ?? 0)} ${f(c2[0] ?? 0)},${f(c2[1] ?? 0)} ${f(p2[0])},${f(p2[1])}`;
  }
  const last = at(pts.length - 1);
  return { line, area: `${line} L${f(W)},${f(H)} L0,${f(H)} Z`, x: last[0], y: last[1] };
}

export function Sparkline({
  values,
  tone,
  className,
}: {
  values: number[];
  tone: SparkTone;
  className?: string;
}) {
  const gradient = useId();
  if (values.length < 2) return null;
  const { line, area, x, y } = curve(values);

  return (
    <svg
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      preserveAspectRatio="none"
      className={cn('h-6.5 w-full overflow-visible', TONE[tone], className)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradient})`} />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={x} cy={y} r="2.2" fill="currentColor" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
